/* eslint-disable no-unused-vars */
import { useRef, useEffect, useMemo } from 'react';
import { DeleteFromUpload } from 'assets/icons';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux/es';
import { creativesActions } from 'store/campaign/creatives';
import { ModalHeader, ModalFooter, Buttons, Loader, Search, Separators } from 'components';
import { useSignal, useComputed } from '@preact/signals-react';
import { CreativeImageUploader } from '../creativeImageUploader/CreativeImageUploader';
import { manageCreativeMediaAPI } from 'services/manageCreativeMedia.service';
import { SelectedImgOrVideo } from './SelectedImgOrVideo';
import * as S from './GeneralModalUI.styled';
import { getVideoSrc, checkCreativeList } from 'utils';
import { CreativeVideoUploader } from '../creativeVideoUploader/CreativeVideoUploader';

const availableResolution = {
  image: { resolutionWidth: 512, resolutionHeight: 512 },
  image_landscape: { resolutionWidth: 1200, resolutionHeight: 628 },
};

const videoTypesList = {
  video_landscape: 'landscape',
  video_portrait: 'portrait',
};

const imgTypes = ['image', 'image_landscape'];
const videoTypes = ['video_landscape', 'video_portrait'];

export const GeneralModalUI = ({ handleClose, creativeModalType, selectedFile$ }) => {
  const pba_id = useSelector((state) => state.creatives.creativeAndTrackingData.pba_id);
  const app_package = useSelector((state) => state.creatives.creativeAndTrackingData.app_package);

  const dataToImgFetch = useMemo(
    () => ({
      package_id: app_package,
      pba_id: pba_id,
    }),
    [app_package, pba_id],
  );
  const dataToVideoFetch = useMemo(
    () => ({
      package_id: app_package,
      pba_id: pba_id,
      videoType: videoTypesList[creativeModalType],
    }),
    [app_package, pba_id, creativeModalType],
  );

  const { data: videoList, isLoading: isLoadingVideoList } =
    manageCreativeMediaAPI.useGetCreativeVideosQuery(dataToVideoFetch, {
      skip: !videoTypesList[creativeModalType],
    });

  const { data: landscapeImgList, isLoading: isLoadingLandscapeList } =
    manageCreativeMediaAPI.useGetCreativeImagesQuery(dataToImgFetch, {
      skip: creativeModalType !== 'image_landscape',
    });

  const { data: iconsImgList, isLoading: isLoadingIconsList } =
    manageCreativeMediaAPI.useGetCreativeIconsQuery(dataToImgFetch, {
      skip: creativeModalType !== 'image',
    });

  const isLoading = useMemo(() => {
    return isLoadingLandscapeList || isLoadingIconsList || isLoadingVideoList;
  }, [isLoadingLandscapeList, isLoadingIconsList, isLoadingVideoList]);

  const [deleteImgAction, deleteImgResult] =
    manageCreativeMediaAPI.useRemoveCreativeImageMutation();

  const [deleteVideoAction, deleteVideoResult] =
    manageCreativeMediaAPI.useRemoveCreativeVideoMutation();

  const repositoryItems$ = useSignal([]);

  const isImg = useMemo(() => imgTypes.includes(creativeModalType), [creativeModalType]);
  const isVideo = useMemo(() => videoTypes.includes(creativeModalType), [creativeModalType]);

  useEffect(() => {
    if (iconsImgList) {
      repositoryItems$.value = iconsImgList;
      return;
    }
    if (landscapeImgList) {
      repositoryItems$.value = landscapeImgList;
      return;
    }
    if (videoList) {
      repositoryItems$.value = videoList;
      return;
    }
  }, [iconsImgList, landscapeImgList, videoList]);

  const searchValue$ = useSignal('');
  const selectedImgOrVideo$ = useSignal(false);

  const searchRef = useRef(null);
  const dispatch = useDispatch();

  const filteredItems$ = useComputed(() => {
    if (!searchValue$.value) return repositoryItems$.value;
    const search = searchValue$.value.toLowerCase();
    return repositoryItems$.value.filter((item) => {
      const target = item.file_name || item.name || '';
      return target.toLowerCase().includes(search);
    });
  });

  const onDelete = async () => {
    let res;
    if (isImg) {
      res = await deleteImgAction(selectedImgOrVideo$.value.media_id);
    }
    if (isVideo) {
      res = await deleteVideoAction(selectedImgOrVideo$.value.media_id);
    }
    if (res?.data?.success) {
      selectedImgOrVideo$.value = false;
      return;
    }
    if (res?.error?.data) {
      alert(res.error.data.Message);
      return;
    }
  };

  const saveButtonOnClick = () => {
    if (isImg) {
      dispatch(creativesActions.updateMediaProperty(selectedImgOrVideo$.value));
    }
    if (isVideo) {
      dispatch(
        creativesActions.updateProperty({
          propField: creativeModalType,
          newValue: getVideoSrc(selectedImgOrVideo$.value),
        }),
      );
      dispatch(
        creativesActions.updateProperty({
          propField: `${creativeModalType}_media_id`,
          newValue: selectedImgOrVideo$.value.media_id,
        }),
      );
    }
    checkCreativeList();
    handleClose();
  };

  return (
    <>
      {(deleteImgResult.isLoading || deleteVideoResult.isLoading) && <Loader.Global delay={500} />}
      <ModalHeader title='Creatives manager' close={handleClose} />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 24px',
          width: '800px',
        }}
      >
        {useComputed(() => (
          <Search
            ref={searchRef}
            width={459}
            placeholder='Search by name'
            value={searchValue$.value}
            onChange={(e) => (searchValue$.value = e.target.value)}
            rightIconOnClick={() => (searchValue$.value = '')}
            style={{ height: '48px', width: '100%' }}
          />
        ))}
        <Buttons.TertiarySmall
          id='delete_selected_creative'
          isDisabled={!selectedImgOrVideo$.value}
          icon={<DeleteFromUpload />}
          onClick={onDelete}
          isDestructive={true}
        >
          Delete Selected
        </Buttons.TertiarySmall>
      </div>

      <Separators type={Separators.separatorTypes.small} />

      <div style={{ display: 'flex' }}>
        <div style={{ flexGrow: '1' }}>
          {isImg && (
            <CreativeImageUploader
              resolutionWidth={availableResolution[creativeModalType]?.resolutionWidth}
              resolutionHeight={availableResolution[creativeModalType]?.resolutionHeight}
              selectedImgOrVideo$={selectedImgOrVideo$}
              selectedFile$={selectedFile$}
              filteredItems$={filteredItems$}
              searchValue$={searchValue$}
              isImg={isImg}
              isVideo={isVideo}
            />
          )}
          {isVideo && (
            <CreativeVideoUploader
              creativeModalType={creativeModalType}
              selectedImgOrVideo$={selectedImgOrVideo$}
              filteredItems$={filteredItems$}
              searchValue$={searchValue$}
              isImg={isImg}
              isVideo={isVideo}
            />
          )}
          {/* <Separators type={Separators.separatorTypes.small} /> */}
          {(isLoading || !filteredItems$.value) && (
            <div style={{ height: 400, display: 'grid', placeItems: 'center' }}>
              <Loader.Local delay={0} />
            </div>
          )}
          {!isLoading && !filteredItems$.value?.length && (
            <S.EmptyPage>Upload Creative, please.</S.EmptyPage>
          )}
          {/* {useComputed(() => {
            if (filteredItems$.value?.length) {
              return (
                <S.ItemsContainer>
                  <Each
                    fieldOfIndex='media_id'
                    of={filteredItems$.value}
                    render={(item, index) => {
                      return (
                        <ListItem
                          creativeModalType={creativeModalType}
                          selectedImgOrVideo$={selectedImgOrVideo$}
                          item={item}
                          searchValue$={searchValue$}
                          isImg={isImg}
                          isVideo={isVideo}
                        />
                      );
                    }}
                  />
                </S.ItemsContainer>
              );
            }
          })} */}
        </div>
        {useComputed(() => (
          <SelectedImgOrVideo
            selectedImgOrVideo={selectedImgOrVideo$.value}
            isImg={isImg}
            isVideo={isVideo}
          />
        ))}
      </div>
      <Separators type={Separators.separatorTypes.small} />
      {useComputed(() => (
        <ModalFooter
          saveButtonText='Use this Creative'
          saveButtonOnClick={saveButtonOnClick}
          close={handleClose}
          saveButtonDisabled={!selectedImgOrVideo$.value}
        />
      ))}
    </>
  );
};
